import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, MessageSquare, Heart, MoreVertical, Copy, Edit, Trash2, X } from 'lucide-react';
import { ThreadedMessage } from '../../lib/threadUtils';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ThreadReplyForm } from './ThreadReplyForm';
import { Portal } from '../common/Portal';
import { CommentSection } from './CommentSection';
import { supabase } from '../../lib/supabase';
import { LiveEventInlineCard, InlineEvent } from './LiveEventInlineCard';

interface MessageNodeProps {
  message: ThreadedMessage;
  currentUserId?: string;
  isAdmin: boolean;
  onReply: (content: string, parentId: string) => Promise<void>;
  onToggleLike: (messageId: string) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  onEdit?: (messageId: string, content: string) => Promise<void>;
  onAddComment: (messageId: string, content: string) => Promise<boolean>;
  alwaysExpanded?: boolean;
  hideActions?: boolean;
}

const MessageNode: React.FC<MessageNodeProps> = ({
  message,
  currentUserId,
  isAdmin,
  onReply,
  onToggleLike,
  onDelete,
  onEdit
  ,
  onAddComment,
  alwaysExpanded = false,
  hideActions = false
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const [inlineEvent, setInlineEvent] = useState<InlineEvent | null>(null);
  
  const nodeRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  
  // Compute modal sizing by aspect ratio
  const aspect = imgDims ? imgDims.w / imgDims.h : null;
  const modalSizeClass = aspect == null
    ? 'max-w-[85vw] max-h-[80vh]'
    : aspect > 1.2
      ? 'max-w-[92vw] max-h-[70vh]'
      : aspect < 0.8
        ? 'max-w-[65vw] max-h-[90vh]'
        : 'max-w-[80vw] max-h-[80vh]';
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Close image modal with ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsImageModalOpen(false);
    };
    if (isImageModalOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isImageModalOpen]);

  // Detect and fetch inline event referenced by [event:<id>]
  useEffect(() => {
    const match = message.content.match(/\[event:([a-f0-9-]{6,})\]/i);
    const fetchEvent = async (id: string) => {
      try {
        const { data } = await supabase
          .from('live_events')
          .select('id, title, description, event_date, event_end_date, thumbnail_url, event_url, is_started, is_ended, replay_available')
          .eq('id', id)
          .maybeSingle();
        setInlineEvent((data || null) as InlineEvent | null);
      } catch (e) {
        console.error('Failed to fetch inline event for thread', e);
        setInlineEvent(null);
      }
    };
    if (match?.[1]) fetchEvent(match[1]); else setInlineEvent(null);
  }, [message.content]);
  
  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };
  
  const handleReply = async (content: string) => {
    await onReply(content, message.id);
    setShowReplyForm(false);
  };
  
  const handleEdit = async () => {
    if (onEdit && editContent !== message.content) {
      await onEdit(message.id, editContent);
    }
    setIsEditing(false);
    setShowMenu(false);
  };
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/community?thread=${message.id}`);
    setShowMenu(false);
  };
  
  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this message?')) {
      await onDelete(message.id);
    }
    setShowMenu(false);
  };
  
  const canModify = isAdmin || currentUserId === message.user_id;
  
  // Calculate indentation based on depth
  const indentWidth = 24; // pixels per indent level
  const indentAmount = message.depth * indentWidth;
  
  const displayName = message.profiles?.display_name || message.profiles?.email || `User ${message.user_id.substring(0, 6)}`;

  return (
    <motion.div
      className="message-node"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      style={{ marginLeft: `${indentAmount}px` }}
      layout={!prefersReducedMotion}
    >
      <div 
        ref={nodeRef}
        className="relative flex gap-3 p-3 mb-2 bg-[#151D29]/80 backdrop-blur-lg border border-white/5 rounded-lg"
      >
        {message.children && message.children.length > 0 && (
          <motion.button
            onClick={handleToggleCollapse}
            className="flex items-center justify-center h-6 w-6 rounded-full bg-[#1a2533]/80 hover:bg-[#232f3e] transition-colors"
            whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            animate={{ rotate: collapsed ? 0 : 90 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight size={14} className="text-[#c9fffc]" />
          </motion.button>
        )}
        
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-[#1a2533] flex items-center justify-center flex-shrink-0">
                {message.profiles?.avatar_url ? (
                  <img
                    src={message.profiles.avatar_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-sm text-gray-300">
                    {(message.profiles?.display_name || message.profiles?.email || message.user_id).charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-[#c9fffc]">
                {displayName}
              </div>
              <div className="text-xs text-gray-400">
                {new Date(message.created_at).toLocaleString()}
              </div>
            </div>
            
            {!hideActions && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 rounded-full hover:bg-[#1a2533] transition-colors"
                >
                  <MoreVertical size={16} className="text-gray-400" />
                </button>
                
                {showMenu && (
                  <div 
                    ref={menuRef}
                    className="absolute right-0 top-8 z-10 w-48 py-2 bg-[#1a2533] backdrop-blur-lg border border-white/5 rounded-lg shadow-xl"
                  >
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowReplyForm(true);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#232f3e] transition-colors"
                    >
                      <MessageSquare size={14} />
                      Reply
                    </button>
                    
                    <button
                      onClick={handleCopyLink}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#232f3e] transition-colors"
                    >
                      <Copy size={14} />
                      Copy Link
                    </button>
                    
                    {canModify && (
                      <>
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#232f3e] transition-colors"
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        
                        <button
                          onClick={handleDelete}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {isEditing ? (
            <div className="mb-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full p-2 bg-[#1a2533] text-white rounded border border-white/10 focus:border-[#c9fffc] focus:outline-none resize-y min-h-[100px]"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1 text-sm text-white hover:bg-[#232f3e] rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEdit}
                  className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="text-white whitespace-pre-wrap break-words mb-3">
              {message.content.replace(/\[event:([a-f0-9-]{6,})\]/i, '')}
            </div>
          )}

          {inlineEvent && (
            <LiveEventInlineCard event={inlineEvent} />
          )}

          {message.image_url && (
            <>
              <button
                onClick={() => setIsImageModalOpen(true)}
                className="mt-2 relative inline-block rounded-lg overflow-hidden border border-[#c9fffc]/10 group"
                title="View image"
              >
                <img
                  src={message.image_url}
                  alt="Message attachment"
                  className="w-28 h-28 object-cover"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>

              {isImageModalOpen && (
                <Portal>
                  <div
                    className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setIsImageModalOpen(false)}
                  >
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <img
                        src={message.image_url}
                        alt="Message attachment full"
                        className={`object-contain rounded-lg border border-white/10 ${modalSizeClass}`}
                        onLoad={(e) => setImgDims({ w: (e.target as HTMLImageElement).naturalWidth, h: (e.target as HTMLImageElement).naturalHeight })}
                      />
                      <button
                        onClick={() => setIsImageModalOpen(false)}
                        className="absolute -top-3 -right-3 p-1.5 rounded-full bg-black/70 text-white hover:bg-black/90 border border-white/10"
                        aria-label="Close image"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Portal>
              )}
            </>
          )}
          
          {!hideActions && (
            <div className="flex items-center gap-4">
              <button
                onClick={() => onToggleLike(message.id)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#c9fffc] transition-colors"
              >
                <Heart size={14} />
                {message.like_count || 0}
              </button>
              
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#c9fffc] transition-colors"
              >
                <MessageSquare size={14} />
                Reply
              </button>
            </div>
          )}

          {showReplyForm && (
            <div className="mt-3">
              <ThreadReplyForm 
                onSubmit={handleReply} 
                onCancel={() => setShowReplyForm(false)} 
              />
            </div>
          )}

          {/* Comments for this message */}
          <div className="mt-2">
            <CommentSection
              messageId={message.id}
              comments={message.comments || []}
              onAddComment={onAddComment}
              alwaysExpanded={alwaysExpanded}
            />
          </div>
        </div>
      </div>
      
      {/* Render children */}
      <AnimatePresence>
        {!collapsed && message.children && message.children.length > 0 && (
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, height: 0 }}
            animate={prefersReducedMotion ? {} : { opacity: 1, height: 'auto' }}
            exit={prefersReducedMotion ? {} : { opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {message.children.map((child) => (
              <MessageNode
                key={child.id}
                message={child}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                onReply={onReply}
                onToggleLike={onToggleLike}
                onDelete={onDelete}
                onEdit={onEdit}
                onAddComment={onAddComment}
                alwaysExpanded={alwaysExpanded}
                hideActions={hideActions}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

interface ThreadTreeProps {
  messages: ThreadedMessage[];
  currentUserId?: string;
  isAdmin: boolean;
  onReply: (content: string, parentId: string) => Promise<void>;
  onToggleLike: (messageId: string) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  onEdit?: (messageId: string, content: string) => Promise<void>;
  loading?: boolean;
  onAddComment: (messageId: string, content: string) => Promise<boolean>;
  alwaysExpanded?: boolean;
  hideActions?: boolean;
}

export const ThreadTree: React.FC<ThreadTreeProps> = ({
  messages,
  currentUserId,
  isAdmin,
  onReply,
  onToggleLike,
  onDelete,
  onEdit,
  loading = false,
  onAddComment,
  alwaysExpanded = false,
  hideActions = false
}) => {
  // Virtualization would be implemented here for large thread trees
  // For simplicity, we're rendering everything directly
  
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-24 bg-[#151D29]/60 rounded-lg mb-2"></div>
            <div className="h-20 bg-[#151D29]/60 rounded-lg ml-6 mb-2"></div>
            <div className="h-16 bg-[#151D29]/60 rounded-lg ml-12 mb-2"></div>
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      {messages.map((message) => (
        <MessageNode
          key={message.id}
          message={message}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          onReply={onReply}
          onToggleLike={onToggleLike}
          onDelete={onDelete}
          onEdit={onEdit}
          onAddComment={onAddComment}
          alwaysExpanded={alwaysExpanded}
          hideActions={hideActions}
        />
      ))}
    </div>
  );
};
